@import url('https://fonts.googleapis.com/css2?family=Anton&family=Noto+Sans+JP:wght@400;500;700&family=Oswald:wght@300;400;500;600&family=Zen+Kaku+Gothic+New:wght@500;700;900&display=swap');

@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  --font-display: 'Anton', 'Zen Kaku Gothic New', sans-serif;
  --font-heading: 'Zen Kaku Gothic New', 'Noto Sans JP', sans-serif;
  --font-body: 'Noto Sans JP', sans-serif;
  --font-label: 'Oswald', sans-serif;

  /* background — clean white / neutral gray */
  --background-50: 0.999 0 0;
  --background-100: 0.976 0 0;
  --background-200: 0.946 0 0;
  --background-300: 0.904 0 0;
  --background-400: 0.858 0 0;
  --background-500: 0.794 0 0;
  --background-600: 0.706 0 0;
  --background-700: 0.606 0 0;
  --background-800: 0.506 0 0;
  --background-900: 0.420 0 0;
  --background-950: 0.320 0 0;

  /* foreground — warm ink */
  --foreground-50: 0.958 0.008 82;
  --foreground-100: 0.904 0.010 80;
  --foreground-200: 0.822 0.012 78;
  --foreground-300: 0.702 0.014 76;
  --foreground-400: 0.572 0.016 74;
  --foreground-500: 0.462 0.016 72;
  --foreground-600: 0.372 0.015 70;
  --foreground-700: 0.298 0.014 68;
  --foreground-800: 0.238 0.013 66;
  --foreground-900: 0.188 0.012 64;
  --foreground-950: 0.142 0.011 62;

  /* primary — vermilion (festival energy) */
  --primary-50: 0.974 0.016 46;
  --primary-100: 0.934 0.038 44;
  --primary-200: 0.868 0.072 42;
  --primary-300: 0.772 0.122 39;
  --primary-400: 0.668 0.172 36;
  --primary-500: 0.588 0.198 33;
  --primary-600: 0.512 0.182 32;
  --primary-700: 0.440 0.158 31;
  --primary-800: 0.372 0.132 30;
  --primary-900: 0.302 0.104 29;
  --primary-950: 0.232 0.078 28;

  /* accent — warm mustard / lantern yellow */
  --accent-50: 0.986 0.022 92;
  --accent-100: 0.962 0.052 91;
  --accent-200: 0.922 0.084 89;
  --accent-300: 0.866 0.118 88;
  --accent-400: 0.814 0.144 86;
  --accent-500: 0.776 0.152 85;
  --accent-600: 0.698 0.142 83;
  --accent-700: 0.598 0.122 81;
  --accent-800: 0.502 0.102 79;
  --accent-900: 0.422 0.082 77;
  --accent-950: 0.322 0.062 75;

  /* secondary — deep forest green (food / nature) */
  --secondary-50: 0.966 0.016 152;
  --secondary-100: 0.930 0.030 152;
  --secondary-200: 0.872 0.050 152;
  --secondary-300: 0.792 0.074 154;
  --secondary-400: 0.682 0.096 156;
  --secondary-500: 0.532 0.110 158;
  --secondary-600: 0.462 0.100 158;
  --secondary-700: 0.392 0.086 158;
  --secondary-800: 0.322 0.070 158;
  --secondary-900: 0.262 0.056 158;
  --secondary-950: 0.202 0.042 158;
}

html {
  scroll-behavior: smooth;
  scroll-padding-top: 72px;
}

body {
  font-family: var(--font-body);
  color: oklch(var(--foreground-800));
  background-color: oklch(var(--background-50));
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  overflow-x: hidden;
}

::selection {
  background-color: oklch(var(--accent-300));
  color: oklch(var(--foreground-950));
}

.font-heading {
  font-family: var(--font-heading);
}

.font-display {
  font-family: var(--font-display);
  font-weight: 400;
}

.en-label {
  font-family: var(--font-label);
  letter-spacing: 0.3em;
  font-weight: 400;
}

/* 縦書き（右側固定ボタン用） */
.vertical-jp {
  writing-mode: vertical-rl;
  text-orientation: upright;
  letter-spacing: 0.22em;
}

/* ハニーポット（スパム対策）を目立たない形で隠す */
.hp-field {
  position: absolute !important;
  left: -9999px !important;
  width: 1px;
  height: 1px;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

/* ---- animations ---- */
@keyframes hero-zoom {
  from {
    transform: scale(1.14);
  }
  to {
    transform: scale(1);
  }
}

@keyframes hero-drift {
  0% {
    transform: scale(1) translate3d(0, 0, 0);
  }
  100% {
    transform: scale(1.07) translate3d(-1.2%, -1.4%, 0);
  }
}

@keyframes hero-float {
  0% {
    transform: translate3d(0, 0, 0);
  }
  100% {
    transform: translate3d(0, -14px, 0);
  }
}

@keyframes hero-veil {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes fade-up {
  from {
    opacity: 0;
    transform: translateY(22px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes line-grow {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

@keyframes ken-burns {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.08);
  }
}

@keyframes scroll-dot {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  35% {
    opacity: 1;
  }
  100% {
    transform: translateY(16px);
    opacity: 0;
  }
}

@keyframes marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.animate-hero-zoom {
  animation: hero-zoom 2.6s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.animate-hero-drift {
  animation: hero-drift 26s ease-in-out infinite alternate;
}

.animate-hero-float {
  animation: hero-float 7s ease-in-out infinite alternate;
}

.animate-hero-veil {
  animation: hero-veil 1.8s ease-out forwards;
}

.animate-fade-up {
  opacity: 0;
  animation: fade-up 1.1s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-line-grow {
  transform-origin: left;
  animation: line-grow 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.animate-ken-burns {
  animation: ken-burns 16s ease-out forwards;
}

.animate-scroll-dot {
  animation: scroll-dot 2.1s ease-in-out infinite;
}

.animate-marquee {
  animation: marquee 26s linear infinite;
}

@media (prefers-reduced-motion: reduce) {
  .animate-hero-zoom,
  .animate-hero-drift,
  .animate-hero-float,
  .animate-hero-veil,
  .animate-fade-up,
  .animate-line-grow,
  .animate-ken-burns,
  .animate-scroll-dot,
  .animate-marquee {
    animation: none;
    opacity: 1;
    transform: none;
  }
}